'use client';

import { routing } from '@/i18n/routing';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export default function CookieConsent() {
  const [showModal, setShowModal] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    // Check if user has already made a cookie choice (either true or false)
    if (document.cookie.includes('cookieConsent=')) {
      return;
    }

    // Show the modal immediately
    setShowModal(true);
  }, []);

  const acceptCookies = () => {
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);

    document.cookie = `cookieConsent=true; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
    setShowModal(false);
    console.log('Cookie consent accepted');

    // Update Google Consent Mode
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        analytics_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
      });
    }

    // Update Meta Pixel Consent
    if (typeof window.fbq === 'function') {
      window.fbq('trackCustom', 'ConsentGranted');
    }
  };

  const declineCookies = () => {
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);

    document.cookie = `cookieConsent=false; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
    setShowModal(false);
    console.log('Cookie consent declined');

    // Update Google Consent Mode to deny
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    }
  };

  const isPrivacyPage = pathname?.includes('/privacy');
  const isHomePage =
    pathname === '/' || routing.locales.some((locale) => pathname === `/${locale}`);

  if (!showModal || isPrivacyPage || !isHomePage) return null;

  return (
    <div
      id="cookie-consent-modal"
      className="cookie-consent-modal show"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="fade-in zoom-in flex w-full max-w-[500px] animate-in flex-col items-center gap-6 rounded-2xl bg-[#212120] p-8 text-center text-white shadow-2xl duration-300">
        <span id="cookie-consent-title" className="font-bold text-2xl leading-tight">
          We use cookies
        </span>
        <p className="max-w-[400px] text-[15px] text-gray-300 leading-relaxed">
          We use cookies to enhance your browsing experience and analyze our traffic. By clicking
          &quot;Accept&quot;, you consent to our use of cookies.
        </p>
        <div className="flex w-full flex-col items-center gap-4">
          <button
            type="button"
            className="btn-shine flex h-[48px] w-auto min-w-[160px] transform cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] px-8 font-bold text-[15px] text-white tracking-tight antialiased shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
            onClick={acceptCookies}
            aria-label="Accept cookies"
          >
            Accept Cookies
          </button>
          <div className="flex items-center gap-2 text-[13px] text-gray-400">
            <Link
              href="/privacy"
              className="!text-gray-400 !no-underline hover:!text-white text-[13px] transition-colors hover:underline"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              Learn more about privacy
            </Link>
            <span className="text-gray-600">•</span>
            <button
              type="button"
              className="cursor-pointer border-none bg-transparent p-0 text-[13px] text-gray-400 transition-colors hover:text-white hover:underline"
              onClick={declineCookies}
              aria-label="Decline cookies"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
